import React from 'react';
import MicIcon from '@material-ui/icons/Mic';
import MicOffIcon from '@material-ui/icons/MicOff';
import SpeakerIcon from '@material-ui/icons/Speaker';
import { IconButton } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import styles from './styles.js';
import useDevices from '../../hooks/useDevices';

export default function CombinedAudioButtonLivemeeting({
  toggleAudio,
  hasAudio,
  classes,
  getAudioSource,
  cameraPublishing,
  changeAudioSource,
  changeAudioOutput,
  getCurrentAudioOutput,
}) {
  const title = hasAudio ? 'Disable Microphone' : 'Enable Microphone';
  const localClasses = styles();
  const { deviceInfo } = useDevices();
  const [devicesAvailable, setDevicesAvailable] = React.useState(null);
  const [options, setOptions] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const audioInputRef = React.useRef(null);
  const audioOutputRef = React.useRef(null);
  const [audioInputHover, setAudioInputHover] = React.useState(false);
  const [audioOutputHover, setAudioOutputHover] = React.useState(false);
  const anchorRef = React.useRef(null);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [audioDeviceId, setAudioDeviceId] = React.useState('');
  const [outputDevicesAvailable, setOutputDevicesAvailable] =
    React.useState(null);
  const [outputOptions, setOutputOptions] = React.useState([]);

  const [selectedOutputIndex, setSelectedOutputIndex] = React.useState(0);
  const [audioOutputId, setAudioOutputId] = React.useState('');
  React.useEffect(() => {
    setDevicesAvailable(deviceInfo.audioInputDevices);
    if (cameraPublishing) {
      const currentDeviceId = getAudioSource()?.deviceId;
      getAudioSource().then((id) => setAudioDeviceId(id));

      const indexOfSelectedElement = devicesAvailable?.indexOf(
        devicesAvailable?.find((e) => e.deviceId === currentDeviceId)
      );

      setSelectedIndex(indexOfSelectedElement);
    }
  }, [cameraPublishing, getAudioSource, deviceInfo, devicesAvailable]);

  React.useEffect(() => {
    if (devicesAvailable) {
      const audioDevicesAvailable = devicesAvailable?.map((e) => {
        return e.label;
      });
      setOptions(audioDevicesAvailable);
    }
  }, [devicesAvailable]);

  const handleChangeAudioSource = (event, index) => {
    setSelectedIndex(index);
    setOpen(false);
    const audioDeviceId = devicesAvailable?.find(
      (device) => device.label === event.target.textContent
    ).deviceId;
    changeAudioSource(audioDeviceId);
    setAudioInputHover(false);
  };

  const handleToggle = (e) => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
    setAudioInputHover(false);
    setAudioOutputHover(false);
  };

  React.useEffect(() => {
    setOutputDevicesAvailable(deviceInfo.audioOutputDevices);

    if (cameraPublishing && outputDevicesAvailable) {
      getCurrentAudioOutput().then((id) => setAudioOutputId(id));

      const indexOfSelectedElement = outputDevicesAvailable.indexOf(
        outputDevicesAvailable.find((e) => e.deviceId === audioOutputId)
      );

      setSelectedOutputIndex(indexOfSelectedElement);
    }
  }, [
    cameraPublishing,
    deviceInfo,
    audioOutputId,
    outputDevicesAvailable,
    getCurrentAudioOutput,
  ]);

  React.useEffect(() => {
    if (outputDevicesAvailable) {
      const audioOutputsAvailable = outputDevicesAvailable.map((e) => {
        return e.label;
      });
      setOutputOptions(audioOutputsAvailable);
    }
  }, [outputDevicesAvailable]);

  const handleChangeAudioOutput = (event, index) => {
    setSelectedOutputIndex(index);
    const audioOutputId = outputDevicesAvailable.find(
      (device) => device.label === event.target.textContent
    ).deviceId;
    changeAudioOutput(audioOutputId);
    setAudioOutputHover(false);
    setOpen(false);
  };

  return (
    <>
      <ButtonGroup
        disableElevation
        className={classes.groupButton}
        variant='contained'
        ref={anchorRef}
        aria-label='split button'
      >
        <Tooltip title={title}>
          <IconButton
            color='inherit'
            onClick={toggleAudio}
            className={`${classes.arrowButton} ${
              !hasAudio ? classes.disabledButton : ''
            }`}
          >
            {!hasAudio ? (
              <MicOffIcon fontSize='inherit' />
            ) : (
              <MicIcon fontSize='inherit' />
            )}
          </IconButton>
        </Tooltip>
        <IconButton
          size='small'
          onClick={handleToggle}
          className={classes.arrowButton}
        >
          <ArrowDropDownIcon />
        </IconButton>
      </ButtonGroup>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        style={{ zIndex: 101 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList>
                  <MenuItem
                    ref={audioInputRef}
                    onMouseEnter={() => setAudioInputHover(true)}
                    onMouseLeave={() => setAudioInputHover(false)}
                  >
                    Audio Input
                  </MenuItem>
                  <Popper
                    open={audioInputHover}
                    anchorEl={audioInputRef.current}
                    placement='right-start'
                    disablePortal
                    style={{ zIndex: 102 }}
                  >
                    <Paper
                      style={{ maxHeight: 150, overflowY: 'auto' }}
                      onMouseEnter={() => setAudioInputHover(true)}
                      onMouseLeave={() => setAudioInputHover(false)}
                    >
                      <MenuList>
                        {options.map((option, index) => (
                          <MenuItem
                            key={option}
                            selected={index === selectedIndex}
                            onClick={(event) =>
                              handleChangeAudioSource(event, index)
                            }
                            classes={{
                              selected: localClasses.selected,
                              root: localClasses.root,
                            }}
                          >
                            {option}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Paper>
                  </Popper>
                  <MenuItem
                    ref={audioOutputRef}
                    onMouseEnter={() => setAudioOutputHover(true)}
                    onMouseLeave={() => setAudioOutputHover(false)}
                  >
                    Audio Output
                  </MenuItem>
                  <Popper
                    open={audioOutputHover}
                    anchorEl={audioOutputRef.current}
                    placement='right-start'
                    disablePortal
                    style={{ zIndex: 102 }}
                  >
                    <Paper
                      style={{ maxHeight: 150, overflowY: 'auto' }}
                      onMouseEnter={() => setAudioOutputHover(true)}
                      onMouseLeave={() => setAudioOutputHover(false)}
                    >
                      <MenuList>
                        {outputOptions.map((option, index) => (
                          <MenuItem
                            key={option}
                            selected={index === selectedOutputIndex}
                            onClick={(event) =>
                              handleChangeAudioOutput(event, index)
                            }
                            classes={{ selected: localClasses.selected }}
                          >
                            {option}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Paper>
                  </Popper>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
